/**
 * Email Service
 *
 * Handles all email-related operations for Waitlistly.
 * Delegates actual sending to server-side API routes (keeps Resend API key secure).
 *
 * Three email types:
 * 1. Welcome Email → sent to subscriber when they join the waitlist
 * 2. Owner Notification → sent to project owner when a new lead joins
 * 3. Launch Announcement → bulk email to all subscribers when project launches
 */

import { supabase } from '@/lib/supabase';

export interface EmailConfig {
  email_enabled: boolean;
  email_welcome_subject: string;
  email_welcome_body: string;
  email_notify_owner: boolean;
  email_owner_address: string | null;
  email_from_name: string | null;
  email_launch_subject: string;
  email_launch_body: string;
  email_launch_sent_at: string | null;
  email_welcome_template: string;
  email_owner_template: string;
  email_launch_template: string;
}

export interface WelcomeEmailData {
  to: string;
  subscriberName?: string;
  position: number;
  totalWaitlist: number;
  referralLink: string;
  projectName: string;
  projectUrl: string;
}

export interface OwnerNotificationData {
  leadEmail: string;
  leadName?: string;
  leadPhone?: string;
  totalLeads: number;
  projectName: string;
  dashboardUrl: string;
}

export interface LaunchEmailData {
  projectName: string;
  projectUrl: string;
  customMessage: string;
}

export const emailService = {
  /**
   * Get email configuration for a project
   */
  async getEmailConfig(
    projectId: string
  ): Promise<{ data: EmailConfig | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('product_deploy')
        .select(
          'email_enabled, email_welcome_subject, email_welcome_body, email_notify_owner, email_owner_address, email_from_name, email_launch_subject, email_launch_body, email_launch_sent_at, email_welcome_template, email_owner_template, email_launch_template'
        )
        .eq('project_id', projectId)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Save email configuration for a project
   */
  async saveEmailConfig(
    projectId: string,
    config: Partial<EmailConfig>
  ): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('product_deploy')
        .update({
          ...config,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Trigger welcome email via API route (client-safe, no API key exposure)
   */
  async sendWelcomeEmail(
    projectId: string,
    data: WelcomeEmailData
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/emails/welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, ...data }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send welcome email' };
      }

      return { success: true };
    } catch (error) {
      console.error('[Email] Welcome email error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Trigger owner notification via API route
   */
  async sendOwnerNotification(
    projectId: string,
    data: OwnerNotificationData
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/emails/notify-owner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, ...data }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send notification' };
      }

      return { success: true };
    } catch (error) {
      console.error('[Email] Owner notification error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Trigger launch announcement email (bulk) via API route
   */
  async sendLaunchEmail(
    projectId: string,
    data: LaunchEmailData
  ): Promise<{ success: boolean; sentCount?: number; error?: string }> {
    try {
      const response = await fetch('/api/emails/launch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, ...data }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send launch email' };
      }

      return { success: true, sentCount: result.sentCount };
    } catch (error) {
      console.error('[Email] Launch email error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Send a test welcome email to the owner
   */
  async sendTestEmail(
    projectId: string,
    testEmail: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/emails/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, testEmail }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send test email' };
      }

      return { success: true };
    } catch (error) {
      console.error('[Email] Test email error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
